import { useQuery } from '@tanstack/react-query'
import { useTasks } from '@/hooks/useTasks'
import { tasksApi } from '@/lib/api'
import type { TaskSummary } from '@/types'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Resolve task by display id or uuid from cached list, then fetch if missing. */
export function useWorkerTask(taskId: string | undefined) {
  const listQuery = useTasks()

  const fromList = listQuery.data?.find(
    t => t.id === taskId || t.uuid === taskId,
  )

  const detailQuery = useQuery({
    queryKey: ['task', taskId],
    queryFn: async () => (await tasksApi.getByUuid(taskId!)).data as TaskSummary,
    enabled: !!taskId && listQuery.isSuccess && !fromList && UUID_RE.test(taskId),
  })

  const task = fromList ?? detailQuery.data

  return {
    task,
    isLoading: listQuery.isLoading || (!fromList && !!taskId && detailQuery.isLoading),
    isError: listQuery.isError || detailQuery.isError,
    refetch: detailQuery.refetch,
  }
}

export function useTaskPallets(taskUuid: string | undefined) {
  return useQuery({
    queryKey: ['task-pallets', taskUuid],
    queryFn: async () => {
      if (!taskUuid) throw new Error('uuid required')
      const res = await tasksApi.getPallets(taskUuid, { limit: 500, offset: 0 })
      return res.data.pallets
    },
    enabled: !!taskUuid,
    refetchInterval: 5000,
  })
}

export function useTaskBoxes(taskUuid: string | undefined, palletNum: number | undefined) {
  return useQuery({
    queryKey: ['task-boxes', taskUuid, palletNum],
    queryFn: async () => {
      if (!taskUuid || palletNum == null) throw new Error('uuid and pallet required')
      const res = await tasksApi.getBoxes(taskUuid, { limit: 200, offset: 0, pallet_num: palletNum })
      return res.data.boxes
    },
    enabled: !!taskUuid && palletNum != null,
    refetchInterval: 2000,
  })
}
